import { Accordion, Anchor, Button, Code, Group, Select, Stack, Text } from '@mantine/core'
import { Table } from '@ospace/shared'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDateTime } from '../utils/format'
import { useDeduplicationKindConfig } from '../hooks/useDeduplicationKind'
import { useDeduplicationRunEventsInfinite } from '../hooks/useDeduplicationRunEventsInfinite'
import type { RunEventRow } from '../types'
import { groupDetailPath } from '../utils/paths'
import { ActionBadge, PhaseBadge } from './StatusBadge'
import { HubspotApiDetails } from './HubspotApiDetails'
import {
  actionFromExecuteEvent,
  isHubspotFailureEvent,
  parseHubspotEventMetadata,
} from '../utils/hubspot-event-details'

const TERMINAL_EXECUTE_EVENTS = new Set([
  'company_merged',
  'contact_merged',
  'contact_kept',
  'review_task_created',
  'company_domain_updated',
  'company_deleted',
  'hubspot_completed',
])

const PHASE_OPTIONS = [
  { value: '', label: 'All phases' },
  { value: 'discover', label: 'discover' },
  { value: 'classify', label: 'classify' },
  { value: 'decide', label: 'decide' },
  { value: 'execute', label: 'execute' },
]

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'MERGE', label: 'MERGE' },
  { value: 'UPDATE_URL', label: 'UPDATE_URL' },
  { value: 'FLAG_FOR_REVIEW', label: 'FLAG_FOR_REVIEW' },
  { value: 'CHECK_DUPLICATE_PAIR', label: 'CHECK_DUPLICATE_PAIR' },
]

type Props = {
  runId: string
}

export const RunEventsLog = ({ runId }: Props) => {
  const kindConfig = useDeduplicationKindConfig()
  const [phaseFilter, setPhaseFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [eventFilter, setEventFilter] = useState('')

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useDeduplicationRunEventsInfinite(runId, {
      phase: phaseFilter || undefined,
      action: actionFilter || undefined,
      event: eventFilter || undefined,
    })

  const events = useMemo(() => data?.pages.flatMap((page) => page.events) ?? [], [data])
  const filteredEvents = events

  const columns: ColumnDef<RunEventRow>[] = [
    {
      accessorKey: 'ts',
      header: 'Time',
      cell: (info) => (
        <Link
          to={groupDetailPath(kindConfig.kind, runId, info.row.original.traceId)}
          className='btn btn-sm btn-light-primary'
        >
          <Text size='xs' style={{ whiteSpace: 'nowrap' }}>
            {formatDateTime(info.getValue() as string)}
          </Text>
        </Link>
      ),
    },
    {
      accessorKey: 'stepOrder',
      header: '#',
      cell: (info) => (info.getValue() as number | null) ?? '—',
      size: 60,
    },
    {
      accessorKey: 'phase',
      header: 'Phase',
      size: 80,
      cell: (info) => <PhaseBadge phase={info.getValue() as string} />,
    },
    {
      accessorKey: 'event',
      header: 'Event',
      cell: (info) => (
        <Text size='sm' fw={500}>
          {info.getValue() as string}
        </Text>
      ),
    },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: (info) => <ActionBadge action={info.getValue() as string | null} />,
    },
    {
      id: 'company',
      header: kindConfig.entityLabel,
      cell: (info) => {
        const row = info.row.original
        return (
          <Stack gap={0}>
            <Text size='sm'>{row.companyName ?? '—'}</Text>
            {row.domain && (
              <Text size='xs' c='dimmed'>
                {row.domain}
              </Text>
            )}
          </Stack>
        )
      },
    },
    {
      accessorKey: 'message',
      header: 'Message',
      cell: (info) => {
        const row = info.row.original
        const message = info.getValue() as string | null
        if (isHubspotFailureEvent(row.event, row.metadata)) {
          return (
            <Text size='xs' c='red' lineClamp={2} style={{ maxWidth: 220 }}>
              {message ?? 'HubSpot call failed'}
            </Text>
          )
        }
        if (TERMINAL_EXECUTE_EVENTS.has(row.event)) {
          const action = row.action ?? actionFromExecuteEvent(row.event)
          const details = parseHubspotEventMetadata(row.metadata, {
            companyId: row.companyId,
            companyName: row.companyName,
            domain: row.domain,
            action,
          })
          if (details.hubspotRecordUrl) {
            return (
              <Stack gap={2} style={{ maxWidth: 220 }}>
                <Text size='xs' lineClamp={2}>
                  {message ?? '—'}
                </Text>
                <Anchor
                  href={details.hubspotRecordUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  size='xs'
                >
                  {details.hubspotRecordId
                    ? `Open in HubSpot (${details.hubspotRecordId})`
                    : 'Open in HubSpot'}
                </Anchor>
              </Stack>
            )
          }
        }
        return (
          <Text size='xs' lineClamp={2} style={{ maxWidth: 200 }}>
            {message ?? '—'}
          </Text>
        )
      },
    },
  ]

  return (
    <div className='card'>
      <div className='card-header border-0 pt-5'>
        <Group justify='space-between' wrap='wrap'>
          <h3 className='card-title mb-0'>
            <span className='card-label fw-bolder fs-3'>Event log</span>
          </h3>
          <Text size='sm' c='dimmed'>
            {filteredEvents.length} shown
            {!phaseFilter && !actionFilter && !eventFilter && hasNextPage
              ? ' (load more for older events)'
              : ''}
            {hasNextPage ? ' · more available' : ''}
          </Text>
        </Group>
      </div>
      <div className='card-body py-3'>
        <Group mb='md' wrap='wrap'>
          <Select
            label='Phase'
            data={PHASE_OPTIONS}
            value={phaseFilter}
            onChange={(v) => setPhaseFilter(v ?? '')}
            style={{ minWidth: 140 }}
          />
          <Select
            label='Action'
            data={ACTION_OPTIONS}
            value={actionFilter}
            onChange={(v) => setActionFilter(v ?? '')}
            style={{ minWidth: 160 }}
          />
          <Select
            label='Event contains'
            data={[
              { value: '', label: 'All events' },
              { value: 'company_', label: 'company_*' },
              { value: 'contact_', label: 'contact_*' },
              { value: 'group_', label: 'group_*' },
              { value: 'hubspot_', label: 'hubspot_*' },
              { value: 'duplicate_', label: 'duplicate_*' },
              { value: 'run_', label: 'run_*' },
            ]}
            value={eventFilter}
            onChange={(v) => setEventFilter(v ?? '')}
            style={{ minWidth: 160 }}
          />
        </Group>

        {isError ? (
          <Text c='red' mb='md'>
            Failed to load run events. Check the deduplication API is deployed and you have admin
            access.
          </Text>
        ) : null}

        <Table columns={columns} data={filteredEvents} isLoading={isLoading} hideSearch />

        {filteredEvents.some((e) => isHubspotFailureEvent(e.event, e.metadata)) && (
          <Stack gap='xs' mt='lg'>
            <Text fw={600} size='sm'>
              HubSpot failures — request input & API response
            </Text>
            {filteredEvents
              .filter((e) => isHubspotFailureEvent(e.event, e.metadata))
              .slice(0, 30)
              .map((event) => {
                const details = parseHubspotEventMetadata(event.metadata, {
                  companyId: event.companyId,
                  companyName: event.companyName,
                  domain: event.domain,
                  action: event.action,
                })
                return (
                  <Accordion key={event.eventId} variant='contained' defaultValue={event.eventId}>
                    <Accordion.Item value={event.eventId}>
                      <Accordion.Control>
                        <Text size='sm' c='red'>
                          {formatDateTime(event.ts)} · {event.companyName ?? event.companyId ?? '—'}
                          {event.action ? ` · ${event.action}` : ''}
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <HubspotApiDetails
                          details={details}
                          message={event.message}
                          variant='failure'
                          action={event.action}
                        />
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )
              })}
          </Stack>
        )}

        {filteredEvents.some(
          (e) =>
            TERMINAL_EXECUTE_EVENTS.has(e.event) && e.metadata && Object.keys(e.metadata).length > 0
        ) && (
          <Stack gap='xs' mt='lg'>
            <Text fw={600} size='sm'>
              Terminal HubSpot results — response
            </Text>
            {filteredEvents
              .filter(
                (e) =>
                  TERMINAL_EXECUTE_EVENTS.has(e.event) &&
                  e.metadata &&
                  Object.keys(e.metadata).length > 0
              )
              .slice(0, 30)
              .map((event) => {
                const action = event.action ?? actionFromExecuteEvent(event.event)
                const details = parseHubspotEventMetadata(event.metadata, {
                  companyId: event.companyId,
                  companyName: event.companyName,
                  domain: event.domain,
                  action,
                })
                return (
                  <Accordion key={event.eventId} variant='contained'>
                    <Accordion.Item value={event.eventId}>
                      <Accordion.Control>
                        <Text size='sm'>
                          {formatDateTime(event.ts)} · {event.event}
                          {event.companyName ? ` · ${event.companyName}` : ''}
                          {details.hubspotRecordUrl ? ' · has HubSpot link' : ''}
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <HubspotApiDetails
                          details={details}
                          message={event.message}
                          variant='success'
                          action={action}
                        />
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )
              })}
          </Stack>
        )}

        {filteredEvents.some(
          (e) =>
            e.metadata &&
            Object.keys(e.metadata).length > 0 &&
            !isHubspotFailureEvent(e.event, e.metadata) &&
            !TERMINAL_EXECUTE_EVENTS.has(e.event)
        ) && (
          <Stack gap='xs' mt='md'>
            <Text fw={600} size='sm'>
              Other event metadata
            </Text>
            {filteredEvents
              .filter(
                (e) =>
                  e.metadata &&
                  Object.keys(e.metadata).length > 0 &&
                  !isHubspotFailureEvent(e.event, e.metadata) &&
                  !TERMINAL_EXECUTE_EVENTS.has(e.event)
              )
              .slice(0, 20)
              .map((event) => (
                <Accordion key={event.eventId} variant='contained'>
                  <Accordion.Item value={event.eventId}>
                    <Accordion.Control>
                      {formatDateTime(event.ts)} · {event.phase} · {event.event}
                      {event.companyName ? ` · ${event.companyName}` : ''}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>
                        {JSON.stringify(event.metadata, null, 2)}
                      </Code>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              ))}
          </Stack>
        )}

        {hasNextPage && (
          <Group justify='center' mt='md'>
            <Button variant='light' loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
              Load more events
            </Button>
          </Group>
        )}
      </div>
    </div>
  )
}
